import { useCallback, useEffect, useState } from 'react';

import { GridColDef } from '@mui/x-data-grid-pro';
import { isMobileOnly } from 'react-device-detect';

import useBuildingBookNames from './useBuildingBookNames';
import { Province } from '../@types/KingdomResponse';
import { IntelTabCols, TabCols } from '../@types/Models';
import { ColumnOrder } from '../@types/State';
import {
  mandatoryCols,
  intelAgeCols,
  ovP,
  army,
  off,
  def,
  getBuildings,
  getSciences,
  defHome,
  be,
  goodSpells,
  mana,
  map,
  sotAge,
  infilAge,
  somAge,
  stealth,
  tpa,
  tpaOff,
  tpaDef,
  wages,
  wpa,
  wpaOff,
  wpaDef,
  badSpells,
  dpa,
  dsPoints,
  offHome,
  opa,
  osPoints,
  peons,
  somDef,
  horses,
  trainCredits,
  buildCredits,
  food,
  gcpa,
  prisoners,
  runes,
  solds,
  tb,
  nonPeon,
  thieves,
  wizards,
  dme,
  dspecs,
  leets,
  ospecsTraining,
  dspecsTraining,
  eliteTraining,
  thievesTraining,
  military,
  ome,
  ospecs,
  ppa,
  gcs,
  requests,
  objectives,
  draftTarget,
  draftRate,
  armyAll,
  coolDown,
} from '../component/Intel/columnDefinitions/gridColumnDefinitions';
import { useIntelState } from '../context/IntelState';

const reorderColumns = (cols: GridColDef[], colArray: ColumnOrder[]) => {
  colArray.forEach((o) => {
    const col = cols.find((c) => c.field === o.field);
    if (!col) return;

    const currentIndex = cols.indexOf(col);
    if (currentIndex > -1) {
      cols.splice(currentIndex, 1);
      const adjustedIndex = o.targetIndex;
      cols.splice(adjustedIndex, 0, col);
    }
  });
};

export default function useCols(dataSet: Province[] | undefined) {
  const { intelState } = useIntelState();
  const [bookNames, buildNames] = useBuildingBookNames();
  const [cols, setCols] = useState<IntelTabCols | undefined>(undefined);

  const rebuildCols = useCallback(() => {
    const surveyCols: GridColDef[] = [];
    const scienceCols: GridColDef[] = [];
    let wtCol: GridColDef | undefined = undefined;
    let fortCol: GridColDef | undefined = undefined;
    if (buildNames?.length) {
      const buildings = getBuildings(buildNames, isMobileOnly);
      buildings.forEach((c) => surveyCols.push(c));
      wtCol = buildings.find((b) => b.headerName === 'WTs');
      fortCol = buildings.find((b) => b.headerName === 'Fort');
    }
    if (bookNames?.length) {
      const sciences = getSciences(bookNames);
      sciences.forEach((c) => scienceCols.push(c));
    }
    const colSet: TabCols = {
      overview: [
        ...mandatoryCols,
        ovP,
        army,
        off,
        def,
        defHome,
        peons,
        tpa,
        tpaOff,
        tpaDef,
        wpa,
        wpaOff,
        wpaDef,
        stealth,
        mana,
        map,
        be,
        wages,
        objectives,
        requests,
        goodSpells,
        badSpells,
        coolDown,
        intelAgeCols,
      ],
      war: [
        ...mandatoryCols,
        ovP,
        armyAll,
        off,
        def,
        defHome,
        tpa,
        wpa,
        stealth,
        mana,
        map,
        peons,
        horses,
        offHome,
        somDef,
        osPoints,
        dsPoints,
        opa,
        dpa,
        objectives,
        requests,
        badSpells,
        intelAgeCols,
        infilAge,
        somAge,
        sotAge,
      ],
      resources: [
        ...mandatoryCols,
        peons,
        solds,
        horses,
        prisoners,
        trainCredits,
        buildCredits,
        gcpa,
        gcs,
        food,
        runes,
        tb,
        tpa,
        badSpells,
        sotAge,
      ],
      military: [
        ...mandatoryCols,
        ome,
        dme,
        off,
        def,
        offHome,
        defHome,
        osPoints,
        dsPoints,
        ppa,
        nonPeon,
        thieves,
        wizards,
        peons,
        solds,
        ospecs,
        dspecs,
        leets,
        horses,
        prisoners,
        ospecsTraining,
        dspecsTraining,
        eliteTraining,
        thievesTraining,
        military,
        wages,
        draftTarget,
        draftRate,
        sotAge,
        infilAge,
      ],
      survey: [...mandatoryCols, be, ...surveyCols],
      science: [...mandatoryCols, ...scienceCols],
      all: [
        ...mandatoryCols,
        ovP,
        army,
        armyAll,
        off,
        def,
        offHome,
        defHome,
        somDef,
        osPoints,
        dsPoints,
        ome,
        dme,
        opa,
        dpa,
        tpa,
        tpaOff,
        tpaDef,
        wpa,
        wpaOff,
        wpaDef,
        ppa,
        nonPeon,
        stealth,
        mana,
        map,
        be,
        wages,
        draftTarget,
        draftRate,
        thieves,
        wizards,
        peons,
        solds,
        ospecs,
        dspecs,
        leets,
        horses,
        prisoners,
        military,
        ospecsTraining,
        dspecsTraining,
        eliteTraining,
        thievesTraining,
        trainCredits,
        buildCredits,
        gcpa,
        gcs,
        food,
        runes,
        tb,
        coolDown,
        requests,
        objectives,
        goodSpells,
        badSpells,
        ...surveyCols,
        ...scienceCols,
        intelAgeCols,
        infilAge,
        somAge,
      ],
    };
    if (wtCol) {
      colSet.resources.push(wtCol);
      colSet.war.push(wtCol);
    }
    if (fortCol) {
      colSet.war.push(fortCol);
    }
    const filterEnemyCols = (x: GridColDef): boolean =>
      x.field !== 'discord' &&
      x.field !== 'mana' &&
      x.field !== 'stealth' &&
      x.field !== 'wages' &&
      x.field !== 'creditsTrain' &&
      x.field !== 'creditsBuild';
    const newCols: IntelTabCols = {
      own: colSet,
      enemy: {
        overview: [...colSet.overview.filter(filterEnemyCols)],
        war: [...colSet.war.filter(filterEnemyCols)],
        resources: [...colSet.resources.filter(filterEnemyCols)],
        military: [...colSet.military.filter(filterEnemyCols)],
        survey: [...colSet.survey.filter(filterEnemyCols)],
        science: [...colSet.science.filter(filterEnemyCols)],
        all: [...colSet.all.filter(filterEnemyCols)],
      },
    };
    // Column reordering
    for (const tabKey in intelState.columnOrder) {
      const orderArray = intelState.columnOrder[tabKey];
      if (!orderArray?.length) continue;
      const ownTabCols = newCols.own[tabKey];
      const enemyTabCols = newCols.enemy[tabKey];
      if (ownTabCols) {
        reorderColumns(ownTabCols, orderArray);
      }
      if (enemyTabCols) {
        reorderColumns(enemyTabCols, orderArray);
      }
    }
    setCols(newCols);
  }, [bookNames, buildNames, intelState.columnOrder]);

  useEffect(() => {
    rebuildCols();
  }, [bookNames?.length, buildNames?.length, rebuildCols]);

  return cols;
}
